import { DatePipe } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '@base/base.component';
import { CustomValidators } from '@forms/custom-validators';
import { GetPaymentContemplationReportQueryResponse, ListDealerBirthDatesQueryResponse } from '@models/reports/reports';
import { KeyValueOptions } from '@models/settings/settings';
import { Paginator, PaginatorItems, Table } from '@models/table';
import { ReportsService } from '@services/reports.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { BirthdateDetailsModalComponent } from '../birthdate-report/modals/birthdate-details-modal/birthdate-details-modal.component';
import { PERMITION_CONSORTIUM_CREDIT_ANALISYS_CONTEMPLATION_PAYMENT_EXPORT_PDF, PERMITION_CONSORTIUM_CREDIT_ANALISYS_CONTEMPLATION_PAYMENT_EXPORT_XLSX, PERMITION_CONSORTIUM_CREDIT_ANALISYS_CONTEMPLATION_PAYMENT_READ } from 'src/app/const/permitions';
import { Period } from '@models/period';
import { DateHelper } from '@helpers/data-helper';
import { Comissioned } from '@models/comissioned';
import { formatNumberToLocal } from 'src/app/utils/format-number-to-local';

@Component({
  selector: 'app-contemplation-payment',
  templateUrl: './contemplation-payment.component.html',
  styleUrl: './contemplation-payment.component.scss'
})
export class ContemplationPaymentComponent extends BaseComponent {
  private accessPointChangeSubscription: Subscription;
  private hasDoneFirstSearch: boolean = false;

  public readonly table: Table<any> = this.createTable();


  public dealerOptions: KeyValueOptions[] = []

  public form: FormGroup;
  public loading: boolean = false;
  public isExportingExcel: boolean = false;
  public isExportingPdf: boolean = false;

  public constructor(private readonly _fb: FormBuilder,
    private readonly _datePipe: DatePipe,
    private readonly _reportsService: ReportsService,
  ) {
    super();
  }

  public ngOnInit() {
    this.listDealers();
    this.createForm();
    this.accessPointChangeSubscription = this.acessPointChangeEvent().subscribe(() => this.listDealers());

    if (!this.hasAccessToAnyDealer) {
      this.openDialogMessage("Não foram encontrados nenhum ponto de venda para sua conta, por esse motivo vamos bloquear a pesquisa deste relatório.")
    }
  }

  public get hasAccessToAnyDealer() {
    return this.getAllAllowedDealers().length > 0;
  }

  public get hasPermissionToExportExcel() {
    return this.getPermissions()
      .some(p => [PERMITION_CONSORTIUM_CREDIT_ANALISYS_CONTEMPLATION_PAYMENT_EXPORT_XLSX].includes(p));
  }

  public get hasPermissionToExportPdf() {
    return this.getPermissions()
      .some(p => [PERMITION_CONSORTIUM_CREDIT_ANALISYS_CONTEMPLATION_PAYMENT_EXPORT_PDF].includes(p))
  }

  public get hasPermissionToRead() {
    return this.getPermissions()
      .some(p => [PERMITION_CONSORTIUM_CREDIT_ANALISYS_CONTEMPLATION_PAYMENT_READ].includes(p));
  }

  private createForm() {
    this.form = this._fb.group({
      identifierCode: [0, [Validators.nullValidator]],
      startDate: [DateHelper.SubstractDaysFromDate(DateHelper.currentDate, 30), [Validators.required]],
      endDate: [DateHelper.currentDate, [Validators.required]]
    });

    this.form.setValidators([
      CustomValidators.isStartDateGreaterThanEndDate("startDate", "endDate"),
      CustomValidators.shouldBeRequiredIfFieldIsNotNull("startDate", "endDate"),
      CustomValidators.shouldBeRequiredIfFieldIsNotNull("endDate", "startDate"),
      CustomValidators.periodGreaterThan(30, 'startDate', 'endDate')
    ]);
  }

  public filter(paginator?: Paginator): void {
    if (!this.hasAccessToAnyDealer || this.form.invalid) {
      return;
    }

    this.loading = true;

    if (!paginator) {
      paginator = this.table.paginator;
    }

    const { identifierCode, startDate, endDate } = this.form.value;
    const contemplationPeriod: Period = {
      startDate,
      endDate
    };

    firstValueFrom(
      this._reportsService.getPaymentContemplationReportData(identifierCode, contemplationPeriod, paginator, this.hasDoneFirstSearch)
    )
      .then((resp: PaginatorItems<GetPaymentContemplationReportQueryResponse>) => {
        this.table.data = resp.items;
        this.table.paginator.length = resp.totalCount;
        this.table.paginator.index = paginator.index;
        this.table.paginator.pageSize = paginator.pageSize;
      })
      .finally(() => {
        this.loading = false;
        this.hasDoneFirstSearch = true;
      });
  }

  public async listDealers() {
    const allowedDealersOptions = this.getAllAllowedDealersForCurrentHierachy()
      .filter((item) => item.isEconomicGroup === false)
      .map<KeyValueOptions>(us => ({
        key: `${us.identifierCode} - ${us.description}`,
        value: us.identifierCode
      }));


    this.dealerOptions = [
      {
        key: "Todos",
        value: 0
      }, ...allowedDealersOptions];

    if (this.form) {
      this.form.patchValue({
        identifierCode: 0
      });
    }
  }

  public cleanForm(): void {
    this.form.patchValue({
      identifierCode: "",
      startDate: DateHelper.SubstractDaysFromDate(DateHelper.currentDate, 30),
      endDate: DateHelper.currentDate
    });

    this.table.data = [];
  }

  public async exportWorksheetReport(): Promise<void> {
    this.isExportingExcel = true;
    try {
      const { identifierCode, startDate, endDate } = this.form.value;

      const period: Period = {
        startDate,
        endDate
      };

      const result = await firstValueFrom(this._reportsService.exportPaymentContemplationReportDataXlsx(identifierCode, period));
      this.download(result.fileBase64, result.fileName, result.contentType);
    } finally {
      this.isExportingExcel = false;
    }
  }

  public async exportPdfReport(): Promise<void> {
    this.isExportingPdf = true;
    try {
      const { identifierCode, startDate, endDate } = this.form.value;

      const period: Period = {
        startDate,
        endDate
      };

      const result = await firstValueFrom(this._reportsService.exportPaymentContemplationReportDataPdf(identifierCode, period));

      this.download(result, "pagamento-de-contemplacao.pdf", "application/pdf");
    } finally {
      this.isExportingPdf = false;
    }
  }

  public pageChange(paginator: Paginator): void {
    this.filter(paginator);
  }

  public viewBirthDateDetails(birthDate: ListDealerBirthDatesQueryResponse): void {
    this.openDialog(BirthdateDetailsModalComponent, { data: birthDate.userId });
  }


  private createTable(): Table<any> {
    return {
      headers: [
        {
          key: "group",
          value: "Grupo"
        },
        {
          key: "quota",
          value: "Cota"
        },
        {
          key: "version",
          value: "Versão"
        },
        {
          key: "consortiumName",
          value: "Consorciado"
        },
        {
          key: "beneficiaryName",
          value: "Favorecido"
        },
        {
          key: "beneficiaryDocument",
          value: "CPF/CNPJ Favorecido"
        },
        {
          key: "situation",
          value: "Situação"
        },
        {
          key: "situationDate",
          value: "Data Situação"
        },
        {
          key: "paymentForm",
          value: "Forma Pagamento"
        },
        {
          key: "bank",
          value: "Banco"
        },
        {
          key: "agency",
          value: "Agência"
        },
        {
          key: "account",
          value: "Conta"
        },
        {
          key: "paidValue",
          value: "Valor Pago"
        }
      ],
      data: [],
      style: {
        table: {
          height: "500px",
        },
        data: [
          {
            key: "paidValue",
            transform: (value) => value ? formatNumberToLocal(value) : "N/A"
          },
        ],
      },
      paginator: {
        index: 1,
        itensPerPage: [10, 15, 20, 50, 100],
        pageSize: 10,
      },
      messageWithoutDates: "Não há dados para exibir no momento.",
    };
  }
}
